import BugReportIcon from '@mui/icons-material/BugReport';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { Course, Token, User } from '@/lib/api';
import { useAdminGetCourses } from '@/lib/api/gen/hooks/useAdminGetCourses';
import { useAdminGetUsers } from '@/lib/api/gen/hooks/useAdminGetUsers';
import { useDebugSendCompletionEmail } from '@/lib/api/gen/hooks/useDebugSendCompletionEmail';
import { useDebugSendWelcomeEmail } from '@/lib/api/gen/hooks/useDebugSendWelcomeEmail';
import { decodeToken, getTokenExpiry, LS_ACCESS_TOKEN, LS_REFRESH_TOKEN, setTokens } from '@/lib/auth';
import globals from '@/lib/globals';
import { authActions, authSelectors } from '@/state/auth';

const formatCountdown = (secondsRemaining: number): string => {
    if (secondsRemaining <= 0) return 'expired';
    const m = Math.floor(secondsRemaining / 60);
    const s = secondsRemaining % 60;
    return `${m}m ${s.toString().padStart(2, '0')}s`;
};

const formatExpiry = (exp: number): string => {
    return new Date(exp * 1000).toLocaleTimeString();
};

interface TokenInfoProps {
    label: string;
    token: string | null;
    nowSeconds: number;
}

const TokenInfo = ({ label, token, nowSeconds }: TokenInfoProps) => {
    const exp = token ? getTokenExpiry(token) : 0;
    const secondsRemaining = exp ? exp - nowSeconds : 0;
    const isExpired = secondsRemaining <= 0;
    const isWarning = secondsRemaining > 0 && secondsRemaining < 300;

    return (
        <Box>
            <Typography
                variant="caption"
                sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                }}
            >
                {label}
            </Typography>
            {token ? (
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                        alignItems: 'center',
                        mt: 0.5,
                    }}
                >
                    <Chip
                        size="small"
                        label={isExpired ? 'expired' : formatCountdown(secondsRemaining)}
                        color={isExpired ? 'error' : isWarning ? 'warning' : 'success'}
                        sx={{ fontFamily: 'monospace', fontSize: 11 }}
                    />
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                        }}
                    >
                        {exp ? `expires ${formatExpiry(exp)}` : 'no exp'}
                    </Typography>
                </Stack>
            ) : (
                <Typography
                    variant="caption"
                    sx={{
                        color: 'error.main',
                        display: 'block',
                        mt: 0.5,
                    }}
                >
                    not present
                </Typography>
            )}
        </Box>
    );
};

interface ClaimsViewerProps {
    token: string | null;
}

const ClaimsViewer = ({ token }: ClaimsViewerProps) => {
    const [open, setOpen] = useState(false);
    const claims = token ? decodeToken(token) : null;

    if (!claims) return null;

    return (
        <Box>
            <Stack
                direction="row"
                spacing={0.5}
                onClick={() => setOpen(!open)}
                sx={{
                    alignItems: 'center',
                    cursor: 'pointer',
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                    }}
                >
                    JWT Claims
                </Typography>
                {open ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
            </Stack>
            <Collapse in={open}>
                <Box
                    component="pre"
                    sx={{
                        mt: 0.5,
                        p: 1,
                        bgcolor: 'grey.900',
                        color: 'grey.100',
                        borderRadius: 1,
                        fontSize: 11,
                        fontFamily: 'monospace',
                        overflowX: 'auto',
                        maxHeight: 200,
                        overflowY: 'auto',
                    }}
                >
                    {JSON.stringify(claims, null, 2)}
                </Box>
            </Collapse>
        </Box>
    );
};

interface AuthTabProps {
    accessToken: string | null;
    refreshToken: string | null;
    nowSeconds: number;
    onForceRefresh: () => void;
    onExpireAccessToken: () => void;
    onCopyAccessToken: () => void;
    status: string | null;
}

const AuthTab = ({
    accessToken,
    refreshToken,
    nowSeconds,
    onForceRefresh,
    onExpireAccessToken,
    onCopyAccessToken,
    status,
}: AuthTabProps) => {
    const user = useSelector(authSelectors.getCurrentUser);

    return (
        <Stack spacing={2}>
            {/* User info */}
            <Box>
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                    }}
                >
                    User
                </Typography>
                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {user?.email ?? '—'}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                            fontFamily: 'monospace',
                        }}
                    >
                        id: {user?.id ?? '—'}
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
                        {user?.admin && (
                            <Chip label="platform admin" size="small" color="error" sx={{ fontSize: 10 }} />
                        )}
                    </Stack>
                </Stack>
            </Box>

            <Divider />

            {/* Token info */}
            <TokenInfo label="Access Token" token={accessToken} nowSeconds={nowSeconds} />
            <TokenInfo label="Refresh Token" token={refreshToken} nowSeconds={nowSeconds} />

            <Divider />

            {/* Actions */}
            <Box>
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        display: 'block',
                        mb: 1,
                    }}
                >
                    Actions
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    <Tooltip title="Force token refresh via /api/login/refresh">
                        <Chip
                            icon={<RefreshIcon />}
                            label="Force Refresh"
                            size="small"
                            onClick={onForceRefresh}
                            variant="outlined"
                            color="primary"
                            clickable
                        />
                    </Tooltip>
                    <Tooltip title="Remove access token from localStorage — next API call will trigger refresh">
                        <Chip
                            icon={<TimerOffIcon />}
                            label="Expire Token"
                            size="small"
                            onClick={onExpireAccessToken}
                            variant="outlined"
                            color="warning"
                            clickable
                        />
                    </Tooltip>
                    <Tooltip title="Copy access token to clipboard">
                        <Chip
                            icon={<ContentCopyIcon />}
                            label="Copy"
                            size="small"
                            onClick={onCopyAccessToken}
                            variant="outlined"
                            clickable
                        />
                    </Tooltip>
                </Stack>
            </Box>

            {status && (
                <Typography
                    variant="caption"
                    sx={{
                        color: 'info.main',
                        fontStyle: 'italic',
                    }}
                >
                    {status}
                </Typography>
            )}

            <Divider />

            {/* JWT Claims */}
            <ClaimsViewer token={accessToken} />
        </Stack>
    );
};

const SendCompletionEmailModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
    const [result, setResult] = useState<{ message: string; success: boolean } | null>(null);
    const [userInputValue, setUserInputValue] = useState('');
    const [debouncedUserSearch, setDebouncedUserSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedUserSearch(userInputValue), 300);
        return () => clearTimeout(timer);
    }, [userInputValue]);

    const { data: usersData, isLoading: usersLoading } = useAdminGetUsers(
        { query: { limit: 10, search: debouncedUserSearch || null } },
        { query: { enabled: open } }
    );
    const { data: coursesData, isLoading: coursesLoading } = useAdminGetCourses(
        { query: { limit: 50 } },
        { query: { enabled: open } }
    );
    const { mutate: sendEmail, isPending } = useDebugSendCompletionEmail();

    const handleClose = () => {
        setSelectedUser(null);
        setSelectedCourse(null);
        setResult(null);
        setUserInputValue('');
        setDebouncedUserSearch('');
        onClose();
    };

    const handleSubmit = () => {
        if (!selectedUser || !selectedCourse) return;
        sendEmail(
            { body: { user_id: selectedUser.id, course_id: selectedCourse.id } },
            {
                onSuccess: () => {
                    setResult({ message: 'Email sent successfully', success: true });
                    setTimeout(handleClose, 2000);
                },
                onError: () => setResult({ message: 'Failed to send email', success: false }),
            }
        );
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>Send Course Completion Email</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Autocomplete
                        options={usersData?.items ?? []}
                        loading={usersLoading}
                        value={selectedUser}
                        onChange={(_, v) => setSelectedUser(v)}
                        inputValue={userInputValue}
                        onInputChange={(_, v) => setUserInputValue(v)}
                        filterOptions={(x) => x}
                        getOptionLabel={(u) => `${u.name} (${u.email})`}
                        renderInput={(params) => <TextField {...params} label="User" size="small" />}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        noOptionsText={debouncedUserSearch ? 'No users found' : 'Type to search users'}
                    />
                    <Autocomplete
                        options={coursesData?.items ?? []}
                        loading={coursesLoading}
                        value={selectedCourse}
                        onChange={(_, v) => setSelectedCourse(v)}
                        getOptionLabel={(c) => c.title}
                        renderInput={(params) => <TextField {...params} label="Course" size="small" />}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                    />
                    {result && (
                        <Typography
                            variant="caption"
                            sx={{ color: result.success ? 'success.main' : 'error.main', fontStyle: 'italic' }}
                        >
                            {result.message}
                        </Typography>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} size="small">
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SendIcon />}
                    onClick={handleSubmit}
                    disabled={!selectedUser || !selectedCourse || isPending}
                    size="small"
                >
                    {isPending ? 'Sending…' : 'Send'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const SendWelcomeEmailModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [result, setResult] = useState<{ message: string; success: boolean } | null>(null);
    const [userInputValue, setUserInputValue] = useState('');
    const [debouncedUserSearch, setDebouncedUserSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedUserSearch(userInputValue), 300);
        return () => clearTimeout(timer);
    }, [userInputValue]);

    const { data: usersData, isLoading: usersLoading } = useAdminGetUsers(
        { query: { limit: 10, search: debouncedUserSearch || null } },
        { query: { enabled: open } }
    );
    const { mutate: sendEmail, isPending } = useDebugSendWelcomeEmail();

    const handleClose = () => {
        setSelectedUser(null);
        setResult(null);
        setUserInputValue('');
        setDebouncedUserSearch('');
        onClose();
    };

    const handleSubmit = () => {
        if (!selectedUser) return;
        sendEmail(
            { body: { user_id: selectedUser.id } },
            {
                onSuccess: () => {
                    setResult({ message: 'Email sent successfully', success: true });
                    setTimeout(handleClose, 2000);
                },
                onError: () => setResult({ message: 'Failed to send email', success: false }),
            }
        );
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>Send Welcome Email</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Autocomplete
                        options={usersData?.items ?? []}
                        loading={usersLoading}
                        value={selectedUser}
                        onChange={(_, v) => setSelectedUser(v)}
                        inputValue={userInputValue}
                        onInputChange={(_, v) => setUserInputValue(v)}
                        filterOptions={(x) => x}
                        getOptionLabel={(u) => `${u.name} (${u.email})`}
                        renderInput={(params) => <TextField {...params} label="User" size="small" />}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        noOptionsText={debouncedUserSearch ? 'No users found' : 'Type to search users'}
                    />
                    {result && (
                        <Typography
                            variant="caption"
                            sx={{ color: result.success ? 'success.main' : 'error.main', fontStyle: 'italic' }}
                        >
                            {result.message}
                        </Typography>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} size="small">
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SendIcon />}
                    onClick={handleSubmit}
                    disabled={!selectedUser || isPending}
                    size="small"
                >
                    {isPending ? 'Sending…' : 'Send'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const EmailTab = () => {
    const [completionModalOpen, setCompletionModalOpen] = useState(false);
    const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);

    return (
        <Stack spacing={2}>
            <Box>
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        display: 'block',
                        mb: 1,
                    }}
                >
                    Welcome
                </Typography>
                <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    startIcon={<EmailIcon />}
                    onClick={() => setWelcomeModalOpen(true)}
                >
                    Send Welcome Email
                </Button>
            </Box>
            <Divider />
            <Box>
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        display: 'block',
                        mb: 1,
                    }}
                >
                    Course Completion
                </Typography>
                <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    startIcon={<EmailIcon />}
                    onClick={() => setCompletionModalOpen(true)}
                >
                    Send Completion Email
                </Button>
            </Box>
            <SendWelcomeEmailModal open={welcomeModalOpen} onClose={() => setWelcomeModalOpen(false)} />
            <SendCompletionEmailModal open={completionModalOpen} onClose={() => setCompletionModalOpen(false)} />
        </Stack>
    );
};

interface DebugPanelProps {
    open: boolean;
    onClose: () => void;
}

export const DebugPanel = ({ open, onClose }: DebugPanelProps) => {
    const dispatch = useDispatch();
    const isSuperuser = useSelector(authSelectors.isSuperuser);
    const [tab, setTab] = useState(0);
    const [nowSeconds, setNowSeconds] = useState(Math.floor(Date.now() / 1000));
    const [status, setStatus] = useState<string | null>(null);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [refreshToken, setRefreshToken] = useState<string | null>(null);

    const syncTokens = useCallback(() => {
        setAccessToken(localStorage.getItem(LS_ACCESS_TOKEN));
        setRefreshToken(localStorage.getItem(LS_REFRESH_TOKEN));
    }, []);

    useEffect(() => {
        if (!open) return;
        syncTokens();
        const interval = setInterval(() => {
            setNowSeconds(Math.floor(Date.now() / 1000));
            syncTokens();
        }, 1000);
        return () => clearInterval(interval);
    }, [open, syncTokens]);

    const handleForceRefresh = useCallback(async () => {
        const rt = localStorage.getItem(LS_REFRESH_TOKEN);
        if (!rt) {
            setStatus('No refresh token found');
            return;
        }
        setStatus('Refreshing...');
        try {
            const { data: tokens } = await axios.post<Token>('/api/login/refresh', null, {
                headers: { Authorization: `bearer ${rt}` },
            });
            setTokens({ accessToken: tokens.access_token, refreshToken: tokens.refresh_token });
            dispatch(authActions.authenticated({ token: tokens.access_token }));
            syncTokens();
            setStatus('Refreshed successfully');
        } catch (_e) {
            setStatus('Refresh failed');
        }
        setTimeout(() => setStatus(null), 3000);
    }, [dispatch, syncTokens]);

    const handleExpireAccessToken = useCallback(() => {
        localStorage.removeItem(LS_ACCESS_TOKEN);
        syncTokens();
        setStatus('Access token removed — next request will trigger refresh');
        setTimeout(() => setStatus(null), 4000);
    }, [syncTokens]);

    const handleCopyAccessToken = useCallback(() => {
        const token = localStorage.getItem(LS_ACCESS_TOKEN);
        if (token) {
            navigator.clipboard.writeText(token);
            setStatus('Copied to clipboard');
            setTimeout(() => setStatus(null), 2000);
        }
    }, []);

    if (!open || (!isSuperuser && globals.environment !== 'local')) return null;

    return (
        <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300 }}>
            <Paper
                elevation={8}
                sx={{
                    width: 480,
                    maxHeight: '80vh',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'warning.dark',
                }}
            >
                {/* Header */}
                <Stack
                    direction="row"
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1,
                        bgcolor: 'warning.dark',
                        flexShrink: 0,
                    }}
                >
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <BugReportIcon sx={{ fontSize: 16, color: 'warning.contrastText' }} />
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700,
                                color: 'warning.contrastText',
                                textTransform: 'uppercase',
                                letterSpacing: 1,
                            }}
                        >
                            Debug Panel
                        </Typography>
                    </Stack>
                    <IconButton size="small" onClick={onClose} sx={{ color: 'warning.contrastText', p: 0.5 }}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Stack>

                {/* Tabs */}
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{
                        flexShrink: 0,
                        borderBottom: 1,
                        borderColor: 'divider',
                        minHeight: 36,
                        '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: 12 },
                    }}
                >
                    <Tab label="Auth" />
                    <Tab label="Email" />
                </Tabs>

                {/* Tab content */}
                <Box sx={{ overflowY: 'auto', p: 2, flex: 1 }}>
                    {tab === 0 && (
                        <AuthTab
                            accessToken={accessToken}
                            refreshToken={refreshToken}
                            nowSeconds={nowSeconds}
                            onForceRefresh={handleForceRefresh}
                            onExpireAccessToken={handleExpireAccessToken}
                            onCopyAccessToken={handleCopyAccessToken}
                            status={status}
                        />
                    )}
                    {tab === 1 && <EmailTab />}
                </Box>
            </Paper>
        </Box>
    );
};
